import axios from 'axios';
import Compressor from 'compressorjs';
import gql from 'graphql-tag';
import { ref } from 'vue';
import apolloClient from '@/apollo/client';
import { handledException } from '@/helpers/datadog';
import {
  type CreateImageUploadUrlPayload,
  ImageCategoryEnum,
  ImageMimeTypeEnum,
} from '@/types';
import { readImageDimensions } from './image-dimensions';

export type S3UploadResult = {
  public_id: string;
  secure_url: string;
};

export type S3UploadErrorCode = 'unsupported-type' | 'backend' | 'upload';

export class S3UploadError extends Error {
  code: S3UploadErrorCode;

  constructor(code: S3UploadErrorCode, message: string) {
    super(message);
    this.name = 'S3UploadError';
    this.code = code;
  }
}

// The presigner signs exactly these types; anything else is rejected before we
// ask the backend for a URL.
const mimeTypeEnums: Record<string, ImageMimeTypeEnum> = {
  'image/jpeg': ImageMimeTypeEnum.Jpeg,
  'image/png': ImageMimeTypeEnum.Png,
  'image/gif': ImageMimeTypeEnum.Gif,
  'image/webp': ImageMimeTypeEnum.Webp,
};

// Browsers report HEIC as one of these, or as an empty type with the extension only.
const heicMimeTypes = [
  'image/heic',
  'image/heif',
  'image/heic-sequence',
  'image/heif-sequence',
];
const heicExtension = /\.hei[cf]$/i;

export type ImaginaryCategory = 'portfolio' | 'venue';

type CompressionSettings = {
  maxDimension: number;
  quality: number;
  mimeType: string;
  minWidth?: number;
  minHeight?: number;
};

const categoryEnums: Record<ImaginaryCategory, ImageCategoryEnum> = {
  portfolio: ImageCategoryEnum.Portfolio,
  venue: ImageCategoryEnum.Venue,
};

const compressionSettings: Record<ImaginaryCategory, CompressionSettings> = {
  portfolio: { maxDimension: 1600, quality: 0.8, mimeType: 'auto' },
  venue: {
    maxDimension: 2560,
    quality: 0.9,
    mimeType: 'image/jpeg',
    minWidth: 1920,
    minHeight: 1080,
  },
};

const heicQuality = 0.9;

const createImageUploadUrlMutation = gql`
  mutation createImageUploadUrl($input: CreateImageUploadUrlInput!) {
    createImageUploadUrl(input: $input) {
      uploadUrl
      publicUrl
      errors
    }
  }
`;

// Some browsers report no type at all for HEIC, so the name is the fallback.
export const isHeicFile = (file: File) =>
  heicMimeTypes.includes(file.type.toLowerCase()) ||
  (!file.type && heicExtension.test(file.name));

// Cloudinary converted HEIC on ingest (the portfolio preset forces jpg); S3 stores
// the bytes as sent, so the conversion moves into the browser. libheif is ~3 MB,
// hence the lazy import and the CSP build, which needs no eval.
const convertHeicToJpeg = async (file: File): Promise<File> => {
  const { heicTo } = await import('heic-to/csp');
  const jpeg = await heicTo({
    blob: file,
    type: 'image/jpeg',
    quality: heicQuality,
  });

  return new File([jpeg], `${file.name.replace(heicExtension, '')}.jpg`, {
    type: 'image/jpeg',
  });
};

const resolveMaxDimension = async (
  file: File,
  { maxDimension, minWidth = 0, minHeight = 0 }: CompressionSettings,
) => {
  if (!minWidth && !minHeight) {
    return maxDimension;
  }

  const { width, height } = await readImageDimensions(file);
  const longest = Math.max(width, height);
  const neededScale = Math.max(minWidth / width, minHeight / height);

  return neededScale * longest <= maxDimension
    ? maxDimension
    : Math.min(longest, Math.ceil(neededScale * longest));
};

const compressImage = async (
  file: File,
  settings: CompressionSettings,
): Promise<Blob> => {
  const maxDimension = await resolveMaxDimension(file, settings);

  return new Promise((resolve, reject) => {
    new Compressor(file, {
      maxWidth: maxDimension,
      maxHeight: maxDimension,
      quality: settings.quality,
      mimeType: settings.mimeType,
      success: resolve,
      error: reject,
    });
  });
};

const firstErrorMessage = (errors: unknown): string | null => {
  if (!errors || typeof errors !== 'object') {
    return null;
  }

  for (const value of Object.values(errors as Record<string, unknown>)) {
    if (typeof value === 'string' && value) {
      return value;
    }

    if (Array.isArray(value) && typeof value[0] === 'string') {
      return value[0];
    }
  }

  return null;
};

const assertAllowedType = (mimeType: string): ImageMimeTypeEnum => {
  const mimeTypeEnum = mimeTypeEnums[mimeType];

  if (!mimeTypeEnum) {
    throw new S3UploadError(
      'unsupported-type',
      `Unsupported image type '${mimeType || 'unknown'}'.`,
    );
  }

  return mimeTypeEnum;
};

export const toUploadableFile = async (original: File): Promise<File> => {
  if (!isHeicFile(original)) {
    return original;
  }

  try {
    return await convertHeicToJpeg(original);
  } catch (error) {
    handledException(error, {
      operationName: 'createImageUploadUrl',
      step: 'heic',
    });

    throw new S3UploadError('upload', `Could not convert '${original.name}'.`);
  }
};

export const uploadImageToS3 = async (
  original: File,
  category: ImaginaryCategory = 'portfolio',
): Promise<S3UploadResult> => {
  const file = await toUploadableFile(original);

  assertAllowedType(file.type.toLowerCase());

  let compressed: Blob;

  try {
    compressed = await compressImage(file, compressionSettings[category]);
  } catch (error) {
    handledException(error, {
      operationName: 'createImageUploadUrl',
      step: 'compress',
    });

    throw new S3UploadError('upload', `Could not compress '${file.name}'.`);
  }

  // Compression can change the type (compressorjs turns large PNGs into JPEG),
  // and the signature must cover the bytes we actually send.
  const mimeType = compressed.type.toLowerCase();
  const mimeTypeEnum = assertAllowedType(mimeType);

  const { data } = await apolloClient.mutate<{
    createImageUploadUrl: CreateImageUploadUrlPayload | null;
  }>({
    mutation: createImageUploadUrlMutation,
    variables: {
      input: {
        filename: file.name,
        mimeType: mimeTypeEnum,
        category: categoryEnums[category],
      },
    },
  });

  const payload = data?.createImageUploadUrl;
  const backendError = firstErrorMessage(payload?.errors);

  if (backendError) {
    throw new S3UploadError('backend', backendError);
  }

  if (!payload?.uploadUrl || !payload?.publicUrl) {
    throw new S3UploadError('backend', 'Could not create an upload url.');
  }

  try {
    await axios.put(payload.uploadUrl, compressed, {
      withCredentials: false,
      // Content-Type must be the only custom header: nothing else is signed.
      headers: { 'Content-Type': mimeType },
      transformRequest: [(body) => body],
    });
  } catch (error) {
    handledException(error, {
      operationName: 'createImageUploadUrl',
      step: 'put',
    });

    throw new S3UploadError('upload', `Could not upload '${file.name}'.`);
  }

  return {
    public_id: payload.publicUrl,
    secure_url: payload.publicUrl,
  };
};

export const s3UploadErrorMessage = (
  error: unknown,
  t: (key: string) => string,
): string => {
  if (error instanceof S3UploadError) {
    return error.code === 'backend'
      ? error.message
      : t(
          error.code === 'unsupported-type'
            ? 'global.image_format_not_supported'
            : 'global.image_upload_fail',
        );
  }

  // Apollo rejects top-level GraphQL errors, whose message is the first one
  return error instanceof Error && error.message
    ? error.message
    : t('global.image_upload_fail');
};

export const useS3Upload = () => {
  const isUploading = ref(false);
  const uploadError = ref<Error | null>(null);

  const uploadImage = async (
    file: File,
    category: ImaginaryCategory,
  ): Promise<S3UploadResult> => {
    isUploading.value = true;
    uploadError.value = null;

    try {
      return await uploadImageToS3(file, category);
    } catch (error) {
      uploadError.value =
        error instanceof Error
          ? error
          : new S3UploadError('upload', 'Image upload failed.');

      throw uploadError.value;
    } finally {
      isUploading.value = false;
    }
  };

  return {
    isUploading,
    uploadError,
    uploadImage,
  };
};
